import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ThoughtLeadership = () => {
  const articles = [
    {
      id: 1,
      title: "The Future of Enterprise AI: Trends and Predictions for 2025",
      excerpt: "Explore the emerging AI technologies that will reshape enterprise operations, from autonomous systems to advanced natural language processing.",
      author: "Dr. Sarah Mitchell",
      authorRole: "Chief AI Strategist",
      authorAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
      publishDate: "2025-01-15",
      readTime: "8 min read",
      category: "AI Strategy",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop",
      tags: ["AI", "Enterprise", "Future Tech", "Strategy"]
    },
    {
      id: 2,
      title: "Cybersecurity in the Age of AI: Protecting Against Advanced Threats",
      excerpt: "Learn how artificial intelligence is revolutionizing cybersecurity defense mechanisms and creating new challenges for enterprise security teams.",
      author: "Michael Chen",
      authorRole: "Cybersecurity Director",
      authorAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      publishDate: "2025-01-10",
      readTime: "6 min read",
      category: "Cybersecurity",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop",
      tags: ["Cybersecurity", "AI", "Threat Detection", "Enterprise"]
    },
    {
      id: 3,
      title: "Building Scalable Analytics Platforms: A Technical Deep Dive",
      excerpt: "Discover the architectural patterns and technologies behind high-performance analytics platforms that handle millions of data points daily.",
      author: "Emily Rodriguez",
      authorRole: "Lead Data Architect",
      authorAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      publishDate: "2025-01-05",
      readTime: "12 min read",
      category: "Analytics",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      tags: ["Analytics", "Architecture", "Big Data", "Performance"]
    },
    {
      id: 4,
      title: "Machine Learning Model Optimization: From Concept to Production",
      excerpt: "A comprehensive guide to optimizing ML models for production environments, covering performance tuning, monitoring, and continuous improvement.",
      author: "David Park",
      authorRole: "ML Engineering Lead",
      authorAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      publishDate: "2024-12-28",
      readTime: "10 min read",
      category: "Machine Learning",
      image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600&h=400&fit=crop",
      tags: ["ML", "Optimization", "Production", "DevOps"]
    }
  ];

  const getCategoryColor = (category) => {
    const colorMap = {
      "AI Strategy": "bg-primary/10 text-primary",
      "Cybersecurity": "bg-warning/10 text-warning",
      "Analytics": "bg-accent/10 text-accent",
      "Machine Learning": "bg-secondary/10 text-secondary",
      "Software Development": "bg-success/10 text-success"
    };
    return colorMap[category] || "bg-primary/10 text-primary";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section className="py-20 bg-gradient-to-br from-muted/30 to-primary/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="BookOpen" size={16} />
            <span>Thought Leadership</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Latest Insights & Articles
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Stay ahead of the curve with expert insights on AI, technology trends, and industry best practices from our team of specialists.
          </p>
        </div>

        {/* Featured Article */}
        <div className="mb-12">
          <div className="bg-white rounded-2xl shadow-elevation border border-border overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Image */}
              <div className="relative">
                <Image
                  src={articles[0].image}
                  alt={articles[0].title}
                  className="w-full h-64 lg:h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(articles[0].category)}`}>
                    {articles[0].category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center space-x-4 mb-4">
                  <Image
                    src={articles[0].authorAvatar}
                    alt={articles[0].author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-foreground">{articles[0].author}</div>
                    <div className="text-sm text-muted-foreground">{articles[0].authorRole}</div>
                  </div>
                </div>

                <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4 leading-tight">
                  {articles[0].title}
                </h3>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {articles[0].excerpt}
                </p>

                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>{formatDate(articles[0].publishDate)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>{articles[0].readTime}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {articles[0].tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <Link
                  to="/services-overview"
                  className="inline-flex items-center space-x-2 text-primary font-semibold hover:space-x-3 transition-all"
                >
                  <span>Read Full Article</span>
                  <Icon name="ArrowRight" size={16} className="hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Article Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {articles.slice(1).map((article) => (
            <Link
              key={article.id}
              to="/services-overview"
              className="group bg-white rounded-xl border border-border hover:border-primary/30 shadow-soft hover:shadow-elevation transition-all duration-300 overflow-hidden hover:-translate-y-1"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <Image
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
                    {article.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Image
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">{article.author}</div>
                    <div className="text-xs text-muted-foreground">{article.authorRole}</div>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
                  {article.title}
                </h3>

                <p className="text-muted-foreground mb-4 text-sm leading-relaxed line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={12} />
                    <span>{formatDate(article.publishDate)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={12} />
                    <span>{article.readTime}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-primary font-medium group-hover:space-x-3 transition-all">
                  <span className="text-sm">Read More</span>
                  <Icon name="ArrowRight" size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="bg-white rounded-2xl shadow-elevation border border-border p-8 lg:p-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Mail" size={32} className="text-primary" />
            </div>
            
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Stay Updated with AI Insights
            </h3>
            
            <p className="text-muted-foreground mb-8">
              Get the latest articles, industry insights, and expert analysis delivered directly to your inbox. Join 5,000+ professionals staying ahead of the AI curve.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-secondary transition-colors whitespace-nowrap">
                Subscribe
              </button>
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              No spam, unsubscribe at any time. Read our privacy policy.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThoughtLeadership;